import {
  Badge,
  Button,
  Card,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import {
  isVesselSubjectType,
  jurisdictionFieldLabel,
  JURISDICTIONS,
  SUBJECT_TYPE_LABELS,
} from '@/constants';
import type { ConversationAttachment } from '@/types/attachment';
import type { DirectExtractedSubject, ReviewCandidate } from '@/types/direct-request';
import type { ReportConfigSummary } from '@/types/report-config';
import { SCREENING_CONFIG_ID } from '@/types/team-settings';

const JURISDICTION_OPTIONS = JURISDICTIONS.map((j) => ({ value: j.alpha2code, label: j.name }));

function confidenceColor(confidence?: string | null): string {
  switch ((confidence ?? '').toLowerCase()) {
    case 'high':
      return 'green';
    case 'medium':
      return 'yellow';
    case 'low':
      return 'red';
    default:
      return 'gray';
  }
}

function emptySubject(): DirectExtractedSubject {
  return {
    source_text: '',
    subject_name: '',
    subject_type: null,
    reference: null,
    jurisdiction_codes: [],
  };
}

function SubjectRow({
  subject,
  onChange,
  onRemove,
  removable,
}: Readonly<{
  subject: DirectExtractedSubject;
  onChange: (subject: DirectExtractedSubject) => void;
  onRemove: () => void;
  removable: boolean;
}>) {
  const isVessel = isVesselSubjectType(subject.subject_type);
  const imoNumber = String(subject.additional_info?.imo_number ?? '');
  const setImoNumber = (value: string) => {
    // The IMO rides in additional_info like every other identity detail;
    // an emptied field drops the key rather than sending ''.
    const { imo_number: _dropped, ...rest } = subject.additional_info ?? {};
    const additional_info = value ? { ...rest, imo_number: value } : rest;
    onChange({
      ...subject,
      additional_info: Object.keys(additional_info).length > 0 ? additional_info : null,
    });
  };
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap="xs">
        <Group gap="xs" align="flex-end" wrap="wrap">
          <TextInput
            label="Name"
            value={subject.subject_name}
            onChange={(e) => onChange({ ...subject, subject_name: e.currentTarget.value })}
            error={subject.subject_name.trim() ? undefined : 'Required'}
            style={{ flex: 2, minWidth: 180 }}
          />
          <Select
            label="Type"
            data={[...SUBJECT_TYPE_LABELS]}
            value={subject.subject_type ?? null}
            onChange={(value) => onChange({ ...subject, subject_type: value })}
            clearable
            placeholder="Unknown"
            style={{ flex: 1, minWidth: 120 }}
          />
          <TextInput
            label="Reference"
            value={subject.reference ?? ''}
            onChange={(e) => onChange({ ...subject, reference: e.currentTarget.value || null })}
            style={{ flex: 1, minWidth: 120 }}
          />
          {isVessel && (
            <TextInput
              label="IMO number"
              description="Seven digits — the vessel's identity through renames and re-flagging"
              value={imoNumber}
              onChange={(e) => setImoNumber(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 140 }}
            />
          )}
        </Group>
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <MultiSelect
            label={jurisdictionFieldLabel(subject.subject_type)}
            searchable
            data={JURISDICTION_OPTIONS}
            value={subject.jurisdiction_codes ?? []}
            onChange={(codes) => onChange({ ...subject, jurisdiction_codes: codes })}
            style={{ flex: 1 }}
          />
          {removable && (
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              leftSection={<IconTrash size={14} />}
              onClick={onRemove}
              aria-label={`Remove subject ${subject.subject_name || '(unnamed)'}`}
            >
              Remove
            </Button>
          )}
        </Group>
        {subject.source_text && subject.source_text !== subject.subject_name && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            From: {subject.source_text}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

export interface RequestReviewListProps {
  candidates: ReviewCandidate[];
  onChange: (candidates: ReviewCandidate[]) => void;
  configs: ReportConfigSummary[];
  files: ConversationAttachment[];
  /** Admin-only: mark manual / Backoffice-docx fulfilment configs. */
  markLegacy?: boolean;
  /** Screening accounts: a config-less candidate IS the Screening pick —
   *  the type select offers "Screening" and nothing is required. */
  offersScreening?: boolean;
}

/** Editable review of extraction candidates before confirm. Each card is
 *  one prospective report request: its config, its attached files and its
 *  editable subject rows. */
export function RequestReviewList({
  candidates,
  onChange,
  configs,
  files,
  markLegacy = false,
  offersScreening = false,
}: Readonly<RequestReviewListProps>) {
  const configOptions = [
    // Screening is not a config — a screening order is a config-less
    // request; the sentinel maps to null at the boundary below.
    ...(offersScreening ? [{ value: SCREENING_CONFIG_ID, label: 'Screening' }] : []),
    ...configs.map((c) => ({
      value: c.id,
      label: markLegacy && c.legacyFlow ? `${c.name} · legacy` : c.name,
    })),
  ];
  const fileOptions = files.map((f) => ({ value: f.id, label: f.filename }));

  const updateCandidate = (index: number, updated: ReviewCandidate) => {
    onChange(candidates.map((c, i) => (i === index ? updated : c)));
  };

  const removeCandidate = (index: number) => {
    onChange(candidates.filter((_, i) => i !== index));
  };

  return (
    <Stack gap="md" w="100%">
      {candidates.map((candidate, index) => (
        <Card
          // Candidates have no stable id; index identity is fine for an
          // in-place edited list that only shrinks via explicit removal.
          key={index}
          withBorder
          padding="md"
          radius="md"
          data-testid={`review-candidate-${index}`}
        >
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  {candidates.length > 1 ? `Request ${index + 1}` : 'Request'}
                </Text>
                {candidate.confidence && (
                  <Badge size="xs" variant="light" color={confidenceColor(candidate.confidence)}>
                    {candidate.confidence} confidence
                  </Badge>
                )}
              </Group>
              {candidates.length > 1 && (
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-sm"
                  onClick={() => removeCandidate(index)}
                  aria-label={`Remove request ${index + 1}`}
                >
                  Remove request
                </Button>
              )}
            </Group>

            {candidate.reasoning && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {candidate.reasoning}
              </Text>
            )}

            <Select
              label="Report type"
              placeholder="Pick a report type"
              data={configOptions}
              value={candidate.report_config_id ?? (offersScreening ? SCREENING_CONFIG_ID : null)}
              onChange={(value) =>
                updateCandidate(index, {
                  ...candidate,
                  report_config_id: value === SCREENING_CONFIG_ID ? null : value,
                })
              }
              // Teams with configs must pick one before confirm — the
              // page disables the button; this is the inline pointer.
              // Screening accounts: config-less IS the Screening pick.
              error={
                configs.length > 0 && !offersScreening && !candidate.report_config_id
                  ? 'Pick a report type for this request'
                  : undefined
              }
              clearable={!offersScreening}
              searchable
            />

            {files.length > 0 && (
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <MultiSelect
                  style={{ flex: 1 }}
                  label="Attached files"
                  description="Files sent to the analyst with this request"
                  data={fileOptions}
                  value={candidate.attachment_ids ?? []}
                  onChange={(ids) => updateCandidate(index, { ...candidate, attachment_ids: ids })}
                />
                <Button
                  size="xs"
                  variant="subtle"
                  data-testid={`attach-files-select-all-${index}`}
                  disabled={(candidate.attachment_ids ?? []).length === files.length}
                  onClick={() =>
                    updateCandidate(index, {
                      ...candidate,
                      attachment_ids: fileOptions.map((o) => o.value),
                    })
                  }
                >
                  Attach all
                </Button>
              </Group>
            )}

            {candidate.subjects.map((subject, subjectIndex) => (
              <SubjectRow
                key={subjectIndex}
                subject={subject}
                removable={candidate.subjects.length > 1}
                onChange={(updated) =>
                  updateCandidate(index, {
                    ...candidate,
                    subjects: candidate.subjects.map((s, i) => (i === subjectIndex ? updated : s)),
                  })
                }
                onRemove={() =>
                  updateCandidate(index, {
                    ...candidate,
                    subjects: candidate.subjects.filter((_, i) => i !== subjectIndex),
                  })
                }
              />
            ))}

            <Group>
              <Button
                variant="light"
                size="compact-sm"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  updateCandidate(index, {
                    ...candidate,
                    subjects: [...candidate.subjects, emptySubject()],
                  })
                }
              >
                Add subject
              </Button>
            </Group>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
